import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import statesService from '../../services/statesService';

const initialState = {
  states: [],
  isLoading: false,
  error: null,
  success: false,
};

export const fetchStates = createAsyncThunk(
  'states/fetchAll',
  async (_, thunkAPI) => {
    try {
      return await statesService.getAll();
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to fetch states';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const createState = createAsyncThunk(
  'states/create',
  async (data, thunkAPI) => {
    try {
      return await statesService.create(data);
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to create state';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const updateState = createAsyncThunk(
  'states/update',
  async ({ id, data }, thunkAPI) => {
    try {
      return await statesService.update(id, data);
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to update state';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const deleteState = createAsyncThunk(
  'states/delete',
  async (id, thunkAPI) => {
    try {
      await statesService.delete(id);
      return id;
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to delete state';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

const statesSlice = createSlice({
  name: 'states',
  initialState,
  reducers: {
    resetSuccess: (state) => {
      state.success = false;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchStates.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchStates.fulfilled, (state, action) => {
        state.isLoading = false;
        state.states = action.payload;
      })
      .addCase(fetchStates.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(createState.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createState.fulfilled, (state, action) => {
        state.isLoading = false;
        state.states.push(action.payload);
        state.success = true;
      })
      .addCase(createState.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updateState.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateState.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.states.findIndex((s) => s._id === action.payload._id);
        if (index !== -1) {
          state.states[index] = action.payload;
        }
        state.success = true;
      })
      .addCase(updateState.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(deleteState.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteState.fulfilled, (state, action) => {
        state.isLoading = false;
        state.states = state.states.filter((s) => s._id !== action.payload);
        state.success = true;
      })
      .addCase(deleteState.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { resetSuccess, clearError } = statesSlice.actions;
export default statesSlice.reducer;
